import { useState } from 'react'
import { Expand, Loader2 } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { formatRelative } from '@/utils/format-date'
import type { BlocoMessage } from '@/types/bloco'

interface BlocoThreadMessagesProps {
  messages: BlocoMessage[]
  loading: boolean
}

const PREVIEW_LENGTH = 180

export function BlocoThreadMessages({ messages, loading }: BlocoThreadMessagesProps) {
  const [expandedMessage, setExpandedMessage] = useState<BlocoMessage | null>(null)

  if (loading) {
    return (
      <div className='flex flex-col items-center justify-center gap-2 py-8 text-text-muted'>
        <Loader2 size={20} className='animate-spin' />
        <p className='text-sm'>Carregando conversa…</p>
      </div>
    )
  }

  if (messages.length === 0) {
    return (
      <p className='py-6 text-center text-sm text-text-muted'>
        Nenhuma mensagem ainda. Inicie a conversa com sua equipe.
      </p>
    )
  }

  return (
    <>
      <div className='max-h-[min(420px,50vh)] space-y-3 overflow-y-auto pr-1'>
        {messages.map((message) => {
          const isLong = message.body.length > PREVIEW_LENGTH
          const preview = isLong ? `${message.body.slice(0, PREVIEW_LENGTH).trim()}…` : message.body

          return (
            <article
              key={message.id}
              className='rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] p-3'
            >
              <div className='mb-1 flex items-center justify-between gap-2 text-xs text-text-muted'>
                <span className='font-medium text-text-primary'>{message.author.name}</span>
                <span>{formatRelative(message.createdAt)}</span>
              </div>
              <p className='whitespace-pre-wrap text-sm leading-relaxed text-text-primary'>{preview}</p>
              {isLong && (
                <div className='mt-2 flex justify-end'>
                  <Button
                    size='sm'
                    variant='secondary'
                    icon={<Expand size={14} />}
                    onClick={() => setExpandedMessage(message)}
                  >
                    Abrir mensagem
                  </Button>
                </div>
              )}
            </article>
          )
        })}
      </div>

      <Modal
        open={expandedMessage !== null}
        onClose={() => setExpandedMessage(null)}
        title={expandedMessage?.author.name ?? 'Mensagem'}
        description={
          expandedMessage ? formatRelative(expandedMessage.createdAt) : undefined
        }
        size='2xl'
      >
        {expandedMessage && (
          <p className='whitespace-pre-wrap text-sm leading-relaxed text-text-primary'>
            {expandedMessage.body}
          </p>
        )}
      </Modal>
    </>
  )
}
